import React, { useState } from 'react';
import { Card, Table, Badge, Spinner, Alert, Pagination, Row, Col } from 'react-bootstrap';
import {
  useCustomersWithCreditBalanceQuery,
  useCreditSpendHistoryQuery
} from '../../queries/credit.query';
import { Link } from '@tanstack/react-location';

function CreditReports() {
  const [spendHistoryPage, setSpendHistoryPage] = useState(1);
  const pageSize = 10;

  const {
    data: customersData,
    isLoading: isLoadingCustomers,
    isError: isCustomersError
  } = useCustomersWithCreditBalanceQuery();

  const {
    data: spendHistoryData,
    isLoading: isLoadingHistory,
    isError: isHistoryError
  } = useCreditSpendHistoryQuery({ page: spendHistoryPage, pageSize });

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  return (
    <div className="mb-4">
      <h4>Customer Credits Report</h4>

      <Row>
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Customers with Credit Balances</h5>
            </Card.Header>
            <Card.Body>
              {isLoadingCustomers && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> Loading...
                </div>
              )}
              {isCustomersError && (
                <Alert variant="danger">Failed to load customers with credit balances</Alert>
              )}
              {customersData && (
                <>
                  <div className="d-flex justify-content-between mb-3">
                    <span>
                      <strong>Total Customers:</strong> {customersData.totalCount}
                    </span>
                    <span>
                      <strong>Total Balance:</strong>{' '}
                      <span className="text-success">
                        {formatCurrency(customersData.totalBalance)}
                      </span>
                    </span>
                  </div>
                  {customersData.customers.length > 0 ? (
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th className="text-end">Available Balance</th>
                          <th className="text-end">Total Added</th>
                          <th className="text-end">Total Spent</th>
                          <th className="text-center">Credits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customersData.customers.map((customer) => (
                          <tr key={customer.clientId}>
                            <td>
                              <Link to={`/clients/${customer.clientId}`}>
                                {customer.clientName}
                              </Link>
                            </td>
                            <td className="text-end text-success">
                              {formatCurrency(customer.availableBalance)}
                            </td>
                            <td className="text-end">{formatCurrency(customer.totalAdded)}</td>
                            <td className="text-end">{formatCurrency(customer.totalSpent)}</td>
                            <td className="text-center">
                              <Badge bg="secondary">{customer.creditCount}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted text-center mb-0">No customers with credit balances</p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Credit Spend History</h5>
            </Card.Header>
            <Card.Body>
              {isLoadingHistory && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> Loading...
                </div>
              )}
              {isHistoryError && (
                <Alert variant="danger">Failed to load credit spend history</Alert>
              )}
              {spendHistoryData && (
                <>
                  <p className="text-muted mb-2">
                    Showing {spendHistoryData.spendHistory.length} of {spendHistoryData.totalCount}{' '}
                    records
                  </p>
                  {spendHistoryData.spendHistory.length > 0 ? (
                    <>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Customer</th>
                            <th className="text-end">Amount</th>
                            <th>Source</th>
                            <th>Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {spendHistoryData.spendHistory.map((spend) => (
                            <tr key={spend.id}>
                              <td>{formatDate(spend.spentAt)}</td>
                              <td>
                                <Link to={`/clients/${spend.clientId}`}>{spend.clientName}</Link>
                              </td>
                              <td className="text-end">{formatCurrency(spend.amount)}</td>
                              <td>
                                <Badge
                                  bg={
                                    spend.source === 'ChargeAll'
                                      ? 'primary'
                                      : spend.source === 'GenerateInvoices'
                                      ? 'success'
                                      : spend.source === 'Monitoring'
                                      ? 'info'
                                      : 'secondary'
                                  }>
                                  {spend.source}
                                </Badge>
                              </td>
                              <td>
                                {spend.invoiceFreshbooksId ? `#${spend.invoiceFreshbooksId}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {spendHistoryData.numberOfPages > 1 && (
                        <Pagination className="justify-content-center">
                          <Pagination.First
                            disabled={spendHistoryPage === 1}
                            onClick={() => setSpendHistoryPage(1)}
                          />
                          <Pagination.Prev
                            disabled={spendHistoryPage === 1}
                            onClick={() => setSpendHistoryPage((p) => p - 1)}
                          />
                          <Pagination.Item active>
                            {spendHistoryPage} / {spendHistoryData.numberOfPages}
                          </Pagination.Item>
                          <Pagination.Next
                            disabled={spendHistoryPage === spendHistoryData.numberOfPages}
                            onClick={() => setSpendHistoryPage((p) => p + 1)}
                          />
                          <Pagination.Last
                            disabled={spendHistoryPage === spendHistoryData.numberOfPages}
                            onClick={() => setSpendHistoryPage(spendHistoryData.numberOfPages)}
                          />
                        </Pagination>
                      )}
                    </>
                  ) : (
                    <p className="text-muted text-center mb-0">No credit spend history</p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default CreditReports;
